import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {InvestmentAccount, InvestmentAccountWithType} from "@/integrations/supabase/investment.models";
import logger from "@logger";

/**
 * Investment Account Service manages investment account operations
 */
export class InvestmentAccountService extends BaseService {

    async createInvestmentAccount(request: InvestmentAccountCreateRequest) : Promise<InvestmentAccount> {
        logger.info("Creating investment account", request);
        const investmentAccountData = this.convertRequestToSnakeCase(request);
        const { data, error } = await supabase
            .from('investment_accounts')
            .insert(investmentAccountData)
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Retrieves an investment account given its ID
     * @param {string} id Account ID to retrieve
     * @returns {InvestmentAccount} retrieved bank account
     */
    async fetchInvestmentAccountById(id: string) : Promise<InvestmentAccount> {
        logger.info(`Fetching investment account with ID: ${id}`)
        const { data, error } = await supabase
            .from('investment_accounts')
            .select()
            .eq('id', id)
            .single();

        if (error) {
            await this.handleError(error);
            throw error
        }

        return data;
    }

    async fetchInvestmentAccountsByUser(request: InvestmentAccountFetchRequest) : Promise<InvestmentAccountWithType[]> {
        logger.info("Fetching investment accounts by user", request);
        const { userId, investmentTypeIds } = request;
        const { data, error } = await supabase
            .from('investment_accounts')
            .select(`
              *,
              investment_type:investment_types(*)
            `)
            .eq('user_id', userId)
            .in('investment_type_id', investmentTypeIds)
            .order('created_at', { ascending: false });

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    /**
     * Fetches investment accounts with their types for a given user
     * @param {string} userId User ID to fetch accounts for
     * @returns {InvestmentAccountWithType[]} retrieved investment accounts with their types
     */
    async fetchInvestmentAccountsWithTypes(userId: string): Promise<InvestmentAccountWithType[]> {
        logger.info(`Fetching investment accounts with types for user: ${userId}`)
        const { data, error} = await supabase
            .from('investment_accounts')
            .select(`
              *,
              investment_type:investment_types(*)
            `)
            .order('created_at', {ascending: false});

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    /**
     * Updates an investment account
     * @param {InvestmentAccountUpdateRequest} request request to update an investment account
     * @returns {InvestmentAccount} updated investment account
     */
    async updateInvestmentAccount(request: InvestmentAccountUpdateRequest) : Promise<InvestmentAccount> {
        logger.info("Updating investment account", request);
        const investmentAccountData = this.convertRequestToSnakeCase(request);

        const { data, error } = await supabase
            .from('investment_accounts')
            .update(investmentAccountData)
            .eq('id', request.id)
            .eq('user_id', request.userId)
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }
}

export default new InvestmentAccountService();
